import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { StatisticsSeries } from '@/types/series';
import { factory } from '@/utils/queryFactory';
import type { Compare, QueryProps, StatisticsResource } from '../../types';

type CachedDataProps<Series extends StatisticsSeries, C extends Compare> = {
  resource: StatisticsResource;
  ids?: number[];
  areaIds?: number[];
  compareProps?: Partial<QueryProps<Series, C>>[];
};

const collectAreaIds = <Series extends StatisticsSeries, C extends Compare>({
  resource,
  ids,
  areaIds,
  compareProps,
}: CachedDataProps<Series, C>) => {
  const entries = [{ resource, ids, areaIds }, ...(compareProps ?? [])];
  const collected = entries.flatMap((e) => [
    ...((e.resource ?? resource) === 'areas' ? (e.ids ?? []) : []),
    ...(e.areaIds ?? []),
  ]);
  return [...new Set(collected)];
};

const useCachedData = <Series extends StatisticsSeries, C extends Compare>(props: CachedDataProps<Series, C>) => {
  const areaIdsToFetch = useMemo(() => collectAreaIds(props), [props]);

  const { data: areaData = [], isFetching: isFetchingAreaData } = useQuery({
    ...factory.areas.byId({ ids: areaIdsToFetch }),
    enabled: areaIdsToFetch.length > 0,
    // keep the previous areas while ids are added/removed, so already loaded names don't blank out
    placeholderData: (previousValue) => previousValue,
  });
  const { data: developerData = [] } = useQuery({
    ...factory.sources.all(),
    enabled: props.resource === 'projects' || props.resource === 'developers',
  });
  const { data: agencyData = [] } = useQuery({ ...factory.agencies.all(), enabled: props.resource === 'agencies' });
  const { data: seriesData = [] } = useQuery({
    ...factory.statistics.series.byResource<Series>({ resource: props.resource }),
    enabled: props.resource !== 'areas',
  });

  return { areaData, isFetchingAreaData, developerData, agencyData, seriesData };
};

export { useCachedData };
